// The one place `import.meta.env` is validated as a whole, at module-eval
// time — before `main.tsx` does anything else with it. A Convex deployment
// has no boot to validate everything at (`packages/convex*/convex/lib/env.ts`
// read per key instead, for a deployment that may never configure a
// feature); a Vite build does, because `import.meta.env` is fixed for the
// life of the build. One fail-fast parse turns a runtime `undefined` deep in
// some module into a message a developer sees immediately, before the blank
// page main.tsx's old hand-rolled guard only caught for VITE_CONVEX_URL.
//
// Namespace import, not `import { z }` — zod ships CJS for the node
// condition and the named form resolves to `undefined` under vitest's
// multi-project run (see `packages/shared/src/corpus/selection.ts`).
import type * as z from "zod";
import { envSchema } from "./env.schema";

/**
 * The fields this module actually serves — the whole schema minus the three
 * the logging modules read raw.
 *
 * BOOT VALIDATION IS ONLY FOR WHAT `env` HANDS OUT. `VITE_LOG_BACKEND`,
 * `VITE_LOG_DEBUG` and `VITE_APP_VERSION` are read straight off
 * `import.meta.env` by `src/logging/appLogging.ts`
 * and `src/logging/earlyErrorCapture.ts` (env.schema.ts's header says why the
 * logger cannot import a framework before it exists), and each of those
 * readers is deliberately TOLERANT: `buildPageBackend` warns once and
 * falls back to `"console"` rather than throwing, because a misconfigured log
 * sink must not be what stops the app from booting. Parsing them here took
 * that guarantee back without saying so — `VITE_LOG_BACKEND="axiom "`, the
 * trailing-space shell typo `backend.ts`'s own comment names, failed the enum
 * and threw before `main.tsx` rendered anything, while the field's docstring
 * and `.env.example` both still promised "never a throw".
 *
 * They stay in `envSchema` regardless: the generator and the checker should
 * keep listing them, and the enum is what documents the accepted values.
 */
const bootSchema = envSchema.omit({
  VITE_LOG_BACKEND: true,
  VITE_LOG_DEBUG: true,
  VITE_APP_VERSION: true,
});

/**
 * `import.meta.env`, restricted to the boot fields, with empty strings read
 * as absent.
 *
 * `X=` in a `.env.local` is somebody deferring the decision, not making one —
 * the same rule `@bindery/shared/env`'s `envReader` and `env-check.ts`'s
 * `parseAssignments` apply, so a Vite build must not be the one surface that
 * disagrees. Two things go wrong without it. `VITE_CORPUS_BASE=` parses as
 * `""` and defeats its own `.default("/corpus")`, silently making every
 * corpus URL relative. And normalizing in only ONE of the two passes below is
 * how the parse and the explanation come apart: a field that fails the object
 * parse on `""` but passes the per-field re-parse leaves nothing to describe,
 * and the thrown Error carries an empty message — a blank page with no
 * diagnostic at all, which is the exact failure this module exists to prevent.
 */
function bootSource(): Record<string, unknown> {
  const raw = import.meta.env as Record<string, unknown>;
  const source: Record<string, unknown> = {};
  for (const name of Object.keys(bootSchema.shape)) {
    const value = raw[name];
    if (value !== undefined && value !== "") source[name] = value;
  }
  return source;
}

/**
 * Re-parse field by field so the thrown message names every problem
 * variable and its remediation, not a raw `ZodError` — mirrors
 * `@bindery/shared/env`'s `envReader` ("is not set" vs "is invalid (…)"),
 * which this file cannot use directly because it validates the whole object
 * in one pass rather than one key read at a time.
 *
 * Reads the same normalized `source` the object parse did, so the two cannot
 * reach different verdicts.
 */
function describeFailure(source: Record<string, unknown>): string {
  const problems = Object.entries(bootSchema.shape).flatMap(([name, field]) => {
    const raw = source[name];
    const parsed = field.safeParse(raw);
    if (parsed.success) return [];
    if (raw === undefined) {
      return [
        name === "VITE_CONVEX_URL"
          ? `${name} is not set — run \`bunx convex dev\` to provision it in .env.local`
          : `${name} is not set — set it in .env.local`,
      ];
    }
    return [
      `${name} is invalid (${parsed.error.issues.map((issue) => issue.message).join("; ")})`,
    ];
  });
  return problems.join("; ");
}

export const env: z.infer<typeof bootSchema> = (() => {
  const source = bootSource();
  const result = bootSchema.safeParse(source);
  if (result.success) return result.data;
  // Falls back to zod's own message rather than throwing an empty Error: the
  // two passes agree by construction now, but "the describer found nothing"
  // must never be the thing that costs a developer the diagnostic.
  const described = describeFailure(source);
  throw new Error(described === "" ? result.error.message : described);
})();
